import { useState, useEffect, useRef } from "react";
import { useStore, CATEGORIES, CAT_META, INIT_CAT_STRIP } from "../store/Store";
import ProductCard from "../components/ProductCard";
import ShowcaseCard from "../components/ShowcaseCard";
import SpotlightCard from "../components/SpotlightCard";
import StripCard from "../components/StripCard";
import TagCard from "../components/TagCard";
import NewInCard from "../components/NewInCard";
import TrendyCard from "../components/TrendyCard";
import ArrivalCard from "../components/ArrivalCard";
import PolaroidCard from "../components/PolaroidCard";
import SplitCard from "../components/SplitCard";
import OutlineCard from "../components/OutlineCard";
import CircleCard from "../components/CircleCard";
import BloomCard from "../components/BloomCard";
import VideoProductCard from "../components/VideoProductCard";
import Footer from "../components/Footer";

// Auto-scroll banner — original gradient/image carousel (unchanged, do not redesign)
function HeroBanner() {
  const { heroPhotoDesktop, heroPhotoMobile } = useStore();
  const [isMobile, setIsMobile] = useState(typeof window!=="undefined" ? window.innerWidth <= 900 : false);
  const [showArrow, setShowArrow] = useState(true);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const onScroll = () => setShowArrow(window.scrollY < 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const desktopImg = heroPhotoDesktop || heroPhotoMobile;
  const mobileImg = heroPhotoMobile || heroPhotoDesktop;
  const img = isMobile ? mobileImg : desktopImg;
  if (!img) return null;

  return (
    <div style={{ width:"100%", lineHeight:0, position:"relative" }}>
      <style>{`
        @keyframes scrollArrowBounce { 0%,100%{ transform:translate(-50%,0); } 50%{ transform:translate(-50%,8px); } }
      `}</style>
      <img src={img} alt="" style={{ width:"100%", height:"auto", display:"block" }}/>
      {isMobile && (
        <div style={{
          position:"absolute", left:"50%", bottom:18, transform:"translate(-50%,0)",
          width:38, height:38, borderRadius:"50%",
          background:"rgba(255,255,255,0.5)", backdropFilter:"blur(14px) saturate(180%)", WebkitBackdropFilter:"blur(14px) saturate(180%)",
          border:"1px solid rgba(255,255,255,0.65)", boxShadow:"0 4px 16px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.8)",
          display:"flex", alignItems:"center", justifyContent:"center",
          opacity:showArrow?1:0, pointerEvents:"none", transition:"opacity 0.35s ease",
          animation: showArrow ? "scrollArrowBounce 1.6s ease-in-out infinite" : "none",
        }}>
          <span style={{ fontSize:16, color:"#1a1a2e" }}>⌄</span>
        </div>
      )}
    </div>
  );
}

// Seasonal cream-box banner — a second, secondary promo band (placed further
// down the page, below "Shop Kurtis by Style" — NOT the main hero)
function SeasonalBanner({ onNav }) {
  const { banners } = useStore();
  const [cur, setCur] = useState(0);
  const timer = useRef(null);

  useEffect(()=>{
    timer.current = setInterval(()=>setCur(c=>(c+1)%banners.length), 5000);
    return ()=>clearInterval(timer.current);
  },[banners.length]);

  const go = i => { setCur(i); clearInterval(timer.current); timer.current=setInterval(()=>setCur(c=>(c+1)%banners.length),5000); };
  const b = banners[cur];
  const marqueeText = Array(10).fill("MATCHWELL · EST. 1979 ·").join(" ");

  return (
    <div style={{ padding:"14px clamp(10px,3vw,24px) 6px" }}>
      <style>{`@keyframes mwMarquee{to{transform:translateX(-50%);}}`}</style>
      <div style={{ position:"relative", borderRadius:20, overflow:"hidden", background:b.bg, minHeight:"clamp(260px,36vw,360px)" }}>

        {/* Faint repeating marquee band across the whole card */}
        <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", overflow:"hidden", pointerEvents:"none" }}>
          <div style={{ display:"flex", whiteSpace:"nowrap", fontSize:15, fontWeight:800, letterSpacing:3, color:"rgba(255,255,255,0.14)", animation:"mwMarquee 22s linear infinite", width:"200%" }}>
            <span style={{ width:"50%" }}>{marqueeText}</span>
            <span style={{ width:"50%" }}>{marqueeText}</span>
          </div>
        </div>

        {/* Cream copy box — the single banner content */}
        <div style={{ position:"relative", zIndex:2, background:"#FDF8F0", margin:"clamp(12px,2.4vw,20px)", borderRadius:16, padding:"clamp(28px,5vw,48px) clamp(24px,5vw,56px)", display:"flex", flexDirection:"column", justifyContent:"center", minHeight:"clamp(230px,32vw,320px)", maxWidth:480 }}>
          <div style={{ fontSize:13, fontWeight:800, color:"#EC4899", marginBottom:2 }}>An</div>
          <div style={{ fontFamily:"'Playfair Display',serif", fontSize:"clamp(28px,4.2vw,46px)", fontWeight:900, color:"#1a1a2e", lineHeight:1.02 }}>
            {b.title?.split(" ")[0] || "Autumn"}<br/>
            <span style={{ color:"#d9c7a0" }}>{b.title?.split(" ").slice(1).join(" ") || "Winter"}</span>
          </div>
          <div style={{ fontSize:"clamp(15px,2vw,19px)", color:"#6b625a", fontWeight:600, margin:"2px 0 22px" }}>{b.subtitle}</div>
          <button onClick={()=>onNav("shop")} style={{ display:"inline-flex", alignItems:"center", gap:8, background:"#EC4899", color:"white", fontWeight:700, fontSize:14, padding:"12px 24px", borderRadius:50, border:"none", cursor:"pointer", width:"fit-content", boxShadow:"0 8px 20px rgba(236,73,153,0.35)" }}>
            {b.cta || "Explore more"} →
          </button>
        </div>
      </div>

      {/* Dots */}
      <div style={{ display:"flex", justifyContent:"center", gap:8, marginTop:12 }}>
        {banners.map((_,i)=>(
          <button key={i} onClick={()=>go(i)} style={{ width:i===cur?22:8, height:8, borderRadius:4, background:i===cur?"#EC4899":"#e5d9c3", border:"none", cursor:"pointer", transition:"all 0.3s", padding:0 }}/>
        ))}
      </div>
    </div>
  );
}

// Mini auto-scroll strip
function MiniCatStrip({ onNav }) {
  const { catStrip } = useStore();
  const active = (catStrip||[]).filter(c=>c.active);
  if (!active.length) return null;
  const items = [...active,...active,...active];

  return (
    <div style={{ overflow:"hidden", width:"100%", padding:"8px 0 4px" }}>
      <style>{`
        @keyframes catscroll {
          0% { transform:translateX(0); }
          100% { transform:translateX(-33.333%); }
        }
        .cat-scroll-track {
          display:flex; gap:12px;
          animation:catscroll ${active.length*2.4}s linear infinite;
          width:max-content;
        }
        .cat-scroll-track:hover { animation-play-state:paused; }
      `}</style>
      <div className="cat-scroll-track">
        {items.map((cat,i)=>(
          <div key={i} onClick={()=>onNav("shop",cat.category)}
            style={{ flexShrink:0, width:100, cursor:"pointer", textAlign:"center" }}>
            <div style={{ width:92, height:116, borderRadius:18, overflow:"hidden", margin:"0 auto 6px", border:"2.5px solid #e8d5b7", boxShadow:"0 3px 14px rgba(0,0,0,0.09)", background:"#fdf5e6", position:"relative", transition:"transform 0.3s" }}
              onMouseEnter={e=>e.currentTarget.style.transform="scale(1.06) translateY(-3px)"}
              onMouseLeave={e=>e.currentTarget.style.transform="none"}>
              {cat.img
                ? <img src={cat.img} alt={cat.name} style={{ width:"100%", height:"100%", objectFit:"cover", objectPosition:"top" }} onError={e=>e.target.style.display="none"}/>
                : <div style={{ width:"100%", height:"100%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:32, opacity:0.35 }}>{(CAT_META[cat.category]||{emoji:"👗"}).emoji}</div>
              }
              <div style={{ position:"absolute", bottom:0, left:0, right:0, height:"38%", background:"linear-gradient(to top,rgba(120,80,40,0.55),transparent)" }}/>
              <div style={{ position:"absolute", bottom:5, left:0, right:0, fontSize:9, color:"white", fontWeight:800, textAlign:"center", letterSpacing:0.5, textShadow:"0 1px 3px rgba(0,0,0,0.5)" }}>{cat.name}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Horizontal scroll product section
function VideoRow({ sectionKey, onNav }) {
  const { videoShowcases, products } = useStore();
  const vids = videoShowcases.filter(v => v.active !== false && v.placement === `home:${sectionKey}`);
  if (!vids.length) return null;
  const banners = vids.filter(v=>v.aspectRatio==="banner");
  const others = vids.filter(v=>v.aspectRatio!=="banner");
  return (
    <>
      {banners.map(v=>(
        <div key={v.id} style={{ width:"100%", lineHeight:0 }}>
          <VideoProductCard video={v} product={products.find(p=>p.id===v.productId)} onNav={onNav}/>
        </div>
      ))}
      {others.length>0 && (
        <div style={{ display:"flex", gap:14, overflowX:"auto", padding:"18px clamp(14px,4vw,40px)", scrollbarWidth:"none" }}>
          {others.map(v=>(
            <VideoProductCard key={v.id} video={v} product={products.find(p=>p.id===v.productId)} onNav={onNav}/>
          ))}
        </div>
      )}
    </>
  );
}

function HSection({ title, accent, products, onNav, viewAll, renderCard, cardWidth }) {
  const ref = useRef(null);
  const scroll = d => ref.current && (ref.current.scrollLeft += d*280);
  return (
    <div style={{ padding:"40px 0", position:"relative", overflow:"hidden" }}>
      {/* Faint tinted wash behind the whole section — subtle, not a hard color block */}
      <div style={{ position:"absolute", inset:0, background:`linear-gradient(180deg, ${accent}07, transparent 55%)`, pointerEvents:"none" }}/>

      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"0 clamp(14px,4vw,40px)", marginBottom:20, position:"relative", flexWrap:"wrap", gap:12 }}>
        <div style={{ display:"inline-flex", alignItems:"center", gap:10, background:`linear-gradient(135deg,${accent}14,${accent}05)`, border:`1px solid ${accent}22`, padding:"8px 18px 8px 14px", borderRadius:50 }}>
          <span style={{ width:8, height:8, borderRadius:"50%", background:accent, flexShrink:0, boxShadow:`0 0 0 4px ${accent}22` }}/>
          <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:"clamp(16px,3vw,24px)", fontWeight:900, color:"#1a1a2e", letterSpacing:0.2 }}>{title}</h2>
        </div>
        <div style={{ display:"flex", gap:6, alignItems:"center" }}>
          <button onClick={()=>scroll(-1)} style={{ width:32, height:32, borderRadius:"50%", border:`1px solid ${accent}55`, background:"white", cursor:"pointer", fontSize:13, color:accent, display:"flex", alignItems:"center", justifyContent:"center", boxShadow:`0 2px 8px ${accent}18` }}>‹</button>
          <button onClick={()=>scroll(1)} style={{ width:32, height:32, borderRadius:"50%", border:`1px solid ${accent}55`, background:"white", cursor:"pointer", fontSize:13, color:accent, display:"flex", alignItems:"center", justifyContent:"center", boxShadow:`0 2px 8px ${accent}18` }}>›</button>
          <button onClick={viewAll} style={{ fontSize:11, fontWeight:800, color:"white", background:`linear-gradient(135deg,${accent},${accent}cc)`, border:"none", padding:"8px 18px", borderRadius:20, cursor:"pointer", boxShadow:`0 4px 14px ${accent}44` }}>View All →</button>
        </div>
      </div>
      <div ref={ref} style={{ display:"flex", gap:22, overflowX:"auto", scrollBehavior:"smooth", padding:"4px clamp(14px,4vw,40px) 16px", scrollbarWidth:"none", position:"relative" }}>
        {products.map(p=>(
          <div key={p.id} style={{ flexShrink:0, width:cardWidth||"clamp(250px,30vw,320px)" }}>
            {renderCard(p)}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function HomePage({ onNav }) {
  const { products, recentViewed, featuredIds, catPhotos, homeCollections, occasionTiles, sectionVis, kurtiStyleTiles, announcements, heroPhotoDesktop, heroPhotoMobile, adminWhatsApp } = useStore();

  const featuredProds = products.filter(p=>featuredIds.includes(p.id)&&p.stock>0);
  const hasHeroPhoto = !!(heroPhotoDesktop || heroPhotoMobile);

  return (
    <div className="mobile-zero-top" style={{ background:"white", paddingTop: hasHeroPhoto ? 0 : 64 }}>
      {sectionVis.hero !== false && <HeroBanner onNav={onNav}/>}

      {/* Offer ticker */}
      <VideoRow sectionKey="hero" onNav={onNav}/>
      {sectionVis.ticker !== false && (
      <div style={{ background:"#1a1a2e", padding:"9px 0", overflow:"hidden" }}>
        <div style={{ display:"flex", gap:0, animation:"ticker 22s linear infinite", whiteSpace:"nowrap" }}>
          {[...Array(3)].flatMap(()=>announcements).map((t,i)=>(
            <span key={i} style={{ fontSize:10, letterSpacing:2, textTransform:"uppercase", color:"rgba(255,255,255,0.65)", padding:"0 28px" }}>
              {t}<span style={{ color:"#EC4899", marginLeft:28 }}>✦</span>
            </span>
          ))}
        </div>
      </div>
      )}

      {/* Category Strip + Grid */}
      <VideoRow sectionKey="ticker" onNav={onNav}/>
      {sectionVis.catStrip !== false && (
      <div style={{ padding:"40px clamp(14px,4vw,40px)" }}>
        <div style={{ textAlign:"center", marginBottom:20 }}>
          <div style={{ fontSize:10, letterSpacing:3, textTransform:"uppercase", color:"#EC4899", fontWeight:700, marginBottom:6 }}>SHOP BY CATEGORY</div>
          <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:"clamp(22px,4vw,38px)", fontWeight:900, color:"#1a1a2e" }}>Our Collections</h2>
        </div>
        {/* Auto-scroll strip */}
        <MiniCatStrip onNav={onNav}/>
        {/* Photo grid */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(clamp(140px,18vw,200px),1fr))", gap:14, marginTop:20 }}>
          {CATEGORIES.map(cat=>{
            const m = CAT_META[cat];
            const catImg = catPhotos?.[cat]||INIT_CAT_STRIP.find(c=>c.category===cat)?.img||"";
            return (
              <div key={cat} onClick={()=>onNav("shop",cat)}
                style={{ borderRadius:14, overflow:"hidden", cursor:"pointer", position:"relative", aspectRatio:"3/4", boxShadow:"0 4px 18px rgba(0,0,0,0.1)", transition:"all 0.3s" }}
                onMouseEnter={e=>{ e.currentTarget.style.transform="translateY(-4px)"; e.currentTarget.style.boxShadow="0 12px 32px rgba(0,0,0,0.18)"; }}
                onMouseLeave={e=>{ e.currentTarget.style.transform="none"; e.currentTarget.style.boxShadow="0 4px 18px rgba(0,0,0,0.1)"; }}>
                <div style={{ position:"absolute", inset:0, background:catImg?"none":m.light }}>
                  {catImg
                    ? <img src={catImg} alt={cat} style={{ width:"100%", height:"100%", objectFit:"cover", objectPosition:"top" }} onError={e=>e.target.style.display="none"}/>
                    : <div style={{ width:"100%", height:"100%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:44, opacity:0.25 }}>{m.emoji}</div>
                  }
                </div>
                <div style={{ position:"absolute", inset:0, background:"linear-gradient(to top,rgba(0,0,0,0.72) 0%,rgba(0,0,0,0.08) 55%,transparent 100%)" }}/>
                <div style={{ position:"absolute", bottom:0, left:0, right:0, padding:"14px 12px", textAlign:"center" }}>
                  <div style={{ fontSize:12, fontWeight:900, color:"white", letterSpacing:2, textTransform:"uppercase", marginBottom:6, textShadow:"0 1px 4px rgba(0,0,0,0.5)" }}>{cat}</div>
                  <div style={{ display:"inline-block", background:"white", color:"#1a1a2e", fontSize:9, fontWeight:700, padding:"4px 14px", borderRadius:50 }}>SHOP NOW</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      )}

      {/* Featured */}
      <VideoRow sectionKey="catStrip" onNav={onNav}/>
      {sectionVis.featured !== false && featuredProds.length > 0 && (
        <HSection title="⭐ Featured by Matchwell" accent="#C9A24B" products={featuredProds} onNav={onNav} viewAll={()=>onNav("shop")}
          cardWidth="clamp(230px,26vw,290px)" renderCard={p=><SpotlightCard p={p} onNav={onNav}/>}/>
      )}

      {/* Shop Kurtis by Style — editable from Admin Panel → Kurti Styles */}
      <VideoRow sectionKey="featured" onNav={onNav}/>
      {sectionVis.kurtiStyle !== false && kurtiStyleTiles?.length > 0 && (
        <div style={{ padding:"36px clamp(14px,4vw,40px)", background:"#fafafa" }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:22 }}>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <div style={{ width:4, height:26, background:"#EC4899", borderRadius:2 }}/>
              <div>
                <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:"clamp(18px,3vw,28px)", fontWeight:900, color:"#1a1a2e" }}>Shop Kurtis by Style</h2>
                <p style={{ fontSize:11, color:"#999", marginTop:2 }}>Every style, every occasion</p>
              </div>
            </div>
            <button onClick={()=>onNav("shop","Kurti")} style={{ fontSize:11, fontWeight:700, color:"#EC4899", background:"none", border:"1px solid #EC4899", padding:"7px 18px", borderRadius:50, cursor:"pointer" }}>View All</button>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(clamp(130px,18vw,160px),1fr))", gap:14 }}>
            {kurtiStyleTiles.map(sub=>(
              <div key={sub.id} onClick={()=>onNav("shop","Kurti")}
                style={{ background:"white", borderRadius:14, overflow:"hidden", cursor:"pointer", boxShadow:"0 2px 12px rgba(0,0,0,0.06)", border:"1px solid #f0f0f0", transition:"all 0.3s" }}
                onMouseEnter={e=>{ e.currentTarget.style.transform="translateY(-4px)"; e.currentTarget.style.borderColor="#EC4899"; }}
                onMouseLeave={e=>{ e.currentTarget.style.transform="none"; e.currentTarget.style.borderColor="#f0f0f0"; }}>
                <div style={{ height:"clamp(140px,20vw,190px)", overflow:"hidden" }}>
                  <img src={sub.img} alt={sub.name} style={{ width:"100%", height:"100%", objectFit:"cover", objectPosition:"top", transition:"transform 0.4s" }}
                    onMouseEnter={e=>e.currentTarget.style.transform="scale(1.06)"}
                    onMouseLeave={e=>e.currentTarget.style.transform="scale(1)"}
                    onError={e=>{ e.target.parentElement.style.background="#FDF2F8"; e.target.style.display="none"; }}/>
                </div>
                <div style={{ padding:"10px 12px" }}>
                  <div style={{ fontWeight:800, fontSize:12, color:"#1a1a2e", marginBottom:2 }}>{sub.name}</div>
                  <div style={{ fontSize:11, color:"#EC4899", fontWeight:700 }}>From {sub.from}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Seasonal promo banner */}
      <VideoRow sectionKey="kurtiStyle" onNav={onNav}/>
      {sectionVis.seasonalBanner !== false && <SeasonalBanner onNav={onNav}/>}

      {/* Product sections — fully admin-controlled from Admin Panel → Collections, each with its own visual style */}
      <VideoRow sectionKey="seasonalBanner" onNav={onNav}/>
      {sectionVis.collections !== false && homeCollections.filter(c=>c.active).map(col=>{
        const prods = products.filter(p=>col.productIds.includes(p.id) && p.stock>0);
        if (!prods.length) return null;
        const style = col.style || "wave";
        const configs = {
          wave:   { width:"clamp(250px,30vw,320px)", render:p=><ShowcaseCard p={p} onNav={onNav} accent={col.accent||"#EC4899"}/> },
          strip:  { width:"clamp(200px,24vw,250px)", render:p=><StripCard p={p} onNav={onNav} accent={col.accent||"#EC4899"}/> },
          tag:    { width:"clamp(230px,27vw,280px)", render:p=><TagCard p={p} onNav={onNav} accent={col.accent||"#EC4899"}/> },
          grid:   { width:"clamp(170px,22vw,220px)", render:p=><ProductCard p={p} onNav={onNav} compact/> },
          newin:  { width:"clamp(190px,23vw,235px)", render:p=><NewInCard p={p} onNav={onNav} accent={col.accent||"#EC4899"}/> },
          trendy: { width:"clamp(190px,23vw,235px)", render:p=><TrendyCard p={p} onNav={onNav} accent={col.accent||"#EC4899"}/> },
          polaroid:{ width:"clamp(180px,22vw,215px)", render:p=><PolaroidCard p={p} onNav={onNav} accent={col.accent||"#EC4899"}/> },
          split:  { width:"clamp(200px,24vw,250px)", render:p=><SplitCard p={p} onNav={onNav} accent={col.accent||"#EC4899"}/> },
          outline:{ width:"clamp(190px,23vw,230px)", render:p=><OutlineCard p={p} onNav={onNav} accent={col.accent||"#EC4899"}/> },
          circle: { width:"clamp(190px,23vw,225px)", render:p=><CircleCard p={p} onNav={onNav} accent={col.accent||"#EC4899"}/> },
          bloom:  { width:"clamp(200px,24vw,245px)", render:p=><BloomCard p={p} onNav={onNav} accent={col.accent||"#EC4899"}/> },
        };
        const styleConfig = configs[style] || configs.wave;
        return (
          <HSection key={col.id} title={col.title} accent={col.accent||"#EC4899"} products={prods} onNav={onNav} viewAll={()=>onNav("shop")}
            cardWidth={styleConfig.width} renderCard={styleConfig.render}/>
        );
      })}

      {/* Price-Wise — auto-computed price bucket tiles, no admin setup needed */}
      <VideoRow sectionKey="collections" onNav={onNav}/>
      {sectionVis.priceWise !== false && (() => {
        const tierDefs = [299, 499, 799, 999];
        const used = new Set();
        const tiles = tierDefs.map(tier=>{
          const match = products.filter(p=>p.price<=tier && p.stock>0 && !used.has(p.id)).sort((a,b)=>b.price-a.price)[0];
          if (match) used.add(match.id);
          return match ? { tier, product:match } : null;
        }).filter(Boolean);
        if (!tiles.length) return null;
        return (
          <div style={{ padding:"44px clamp(14px,4vw,40px) 20px" }}>
            <div style={{ textAlign:"center", marginBottom:24 }}>
              <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:"clamp(22px,3.5vw,32px)", fontWeight:900, color:"#1a1a2e", letterSpacing:1 }}>Price-Wise</h2>
              <p style={{ fontSize:10, letterSpacing:3, color:"#999", textTransform:"uppercase", marginTop:4 }}>Cool Collections</p>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))", gap:14, maxWidth:640, margin:"0 auto" }}>
              {tiles.map(({tier,product})=>(
                <div key={tier} onClick={()=>onNav("product",product.id)} style={{ position:"relative", borderRadius:14, overflow:"hidden", aspectRatio:"1/1", cursor:"pointer", background:"#f3e7e2" }}>
                  <img src={product.img} alt="" style={{ width:"100%", height:"100%", objectFit:"cover", objectPosition:"top" }}/>
                  <span style={{ position:"absolute", top:8, left:8, background:"rgba(255,255,255,0.92)", borderRadius:8, padding:"4px 9px", fontSize:10, fontWeight:800, lineHeight:1.15, color:"#EC4899", textAlign:"center" }}>
                    UNDER<br/>₹{tier}
                  </span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* New Arrivals — stacked mini-rows by theme, minimal arrival-card style */}
      <VideoRow sectionKey="priceWise" onNav={onNav}/>
      {sectionVis.newArrivalRows !== false && (() => {
        const rows = [
          { label:"Ethnic Edit",   cats:["Kurti","3 Piece Set"] },
          { label:"Western Edit",  cats:["Western Wear","Cord Set"] },
          { label:"Accessorize",   cats:["Dupatta","Bottom Wear"] },
        ].map(r=>({ ...r, prods: products.filter(p=>r.cats.includes(p.category)&&p.stock>0).slice(0,8) }))
         .filter(r=>r.prods.length>0);
        if (!rows.length) return null;
        return (
          <div style={{ padding:"44px clamp(14px,4vw,40px) 10px" }}>
            <div style={{ display:"flex", alignItems:"baseline", justifyContent:"space-between", marginBottom:6 }}>
              <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:"clamp(20px,3.2vw,28px)", fontWeight:900, color:"#1a1a2e" }}>New Arrivals</h2>
              <span onClick={()=>onNav("shop")} style={{ fontSize:12, fontWeight:700, color:"#EC4899", cursor:"pointer" }}>Exciting deals →</span>
            </div>
            {rows.map(row=>(
              <div key={row.label} style={{ marginTop:22 }}>
                <div style={{ fontSize:12.5, fontWeight:800, color:"#1a1a2e", marginBottom:12 }}>{row.label}</div>
                <div style={{ display:"flex", gap:16, overflowX:"auto", paddingBottom:8, scrollbarWidth:"none" }}>
                  {row.prods.map(p=>(
                    <div key={p.id} style={{ flexShrink:0, width:"clamp(120px,16vw,150px)" }}>
                      <ArrivalCard p={p} onNav={onNav}/>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        );
      })()}

      {/* Occasion Collections — admin-editable from Admin Panel → Occasion */}
      <VideoRow sectionKey="newArrivalRows" onNav={onNav}/>
      {sectionVis.occasion !== false && occasionTiles?.filter(t=>t.active).length > 0 && (
        <div style={{ padding:"44px clamp(14px,4vw,40px) 20px" }}>
          <div style={{ textAlign:"center", marginBottom:24 }}>
            <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:"clamp(22px,3.5vw,32px)", fontWeight:900, color:"#1a1a2e", letterSpacing:1 }}>Occasion</h2>
            <p style={{ fontSize:10, letterSpacing:3, color:"#999", textTransform:"uppercase", marginTop:4 }}>Specific Collections</p>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))", gap:16, maxWidth:640, margin:"0 auto" }}>
            {occasionTiles.filter(t=>t.active).map(t=>(
              <div key={t.id} onClick={()=>onNav("shop",t.category)} style={{ position:"relative", borderRadius:16, overflow:"hidden", aspectRatio:"4/5", cursor:"pointer" }}>
                {t.img
                  ? <img src={t.img} alt={t.title} style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
                  : <div style={{ width:"100%", height:"100%", background:"#eef3f6" }}/>
                }
                <div style={{ position:"absolute", inset:0, background:"linear-gradient(to top, rgba(0,0,0,0.45), transparent 50%)" }}/>
                <div style={{ position:"absolute", bottom:16, left:0, right:0, textAlign:"center", fontFamily:"'Playfair Display',serif", fontSize:22, letterSpacing:3, color:"white", textShadow:"0 2px 8px rgba(0,0,0,0.4)" }}>
                  {t.title?.toUpperCase()}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recently Viewed */}
      <VideoRow sectionKey="occasion" onNav={onNav}/>
      {sectionVis.recentlyViewed !== false && recentViewed.length > 0 && (
        <div style={{ padding:"36px 0", borderTop:"1px solid #f5f5f5" }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, padding:"0 clamp(14px,4vw,40px)", marginBottom:16 }}>
            <div style={{ width:4, height:26, background:"#06B6D4", borderRadius:2 }}/>
            <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:"clamp(17px,3vw,26px)", fontWeight:900, color:"#1a1a2e" }}>Recently Viewed</h2>
          </div>
          <div style={{ display:"flex", gap:14, overflowX:"auto", padding:"4px clamp(14px,4vw,40px) 10px", scrollbarWidth:"none" }}>
            {recentViewed.map(rid=>{
              const rp = products.find(x=>x.id===rid);
              if (!rp) return null;
              return <div key={rid} style={{ flexShrink:0, width:"clamp(140px,20vw,180px)" }}><ProductCard p={rp} onNav={onNav} compact/></div>;
            })}
          </div>
        </div>
      )}

      {/* WhatsApp Banner */}
      <VideoRow sectionKey="recentlyViewed" onNav={onNav}/>
      {sectionVis.whatsappBanner !== false && (
      <div style={{ margin:"0 clamp(14px,4vw,40px) 36px", borderRadius:20, background:"linear-gradient(135deg,#1a1a2e,#2d1b69)", padding:"32px clamp(18px,5vw,52px)", display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:16 }}>
        <div>
          <div style={{ fontFamily:"'Playfair Display',serif", fontSize:"clamp(18px,3vw,32px)", fontWeight:900, color:"white", marginBottom:6 }}>Order on WhatsApp</div>
          <p style={{ fontSize:12, color:"rgba(255,255,255,0.7)" }}>Personal assistance • Fast delivery • Easy returns</p>
        </div>
        <a href={`https://wa.me/${adminWhatsApp}?text=Hi! I want to shop from Matchwell.`} target="_blank" rel="noreferrer"
          style={{ background:"#25D366", color:"white", padding:"12px 28px", borderRadius:50, fontSize:13, fontWeight:800, textDecoration:"none", display:"flex", alignItems:"center", gap:8 }}>
          💬 WhatsApp Now
        </a>
      </div>
      )}

      {/* Showroom Map */}
      <VideoRow sectionKey="whatsappBanner" onNav={onNav}/>
      {sectionVis.showroomMap !== false && (
      <div style={{ margin:"0 clamp(14px,4vw,40px) 36px", borderRadius:18, overflow:"hidden", boxShadow:"0 4px 24px rgba(0,0,0,0.08)" }}>
        <div style={{ background:"linear-gradient(135deg,#1a1a2e,#2d1b69)", padding:"20px clamp(14px,4vw,32px)", display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:14 }}>
          <div>
            <div style={{ fontSize:10, letterSpacing:3, textTransform:"uppercase", color:"#EC4899", fontWeight:700, marginBottom:4 }}>📍 VISIT US</div>
            <div style={{ fontFamily:"'Playfair Display',serif", fontSize:"clamp(16px,3vw,24px)", fontWeight:900, color:"white", marginBottom:2 }}>Matchwell Showroom</div>
            <div style={{ fontSize:12, color:"rgba(255,255,255,0.65)" }}>Gulmandi Corner, Chhatrapati Sambhajinagar, Maharashtra</div>
          </div>
          <a href="https://share.google/7hqMngyBS6s79zTGn" target="_blank" rel="noreferrer"
            style={{ background:"#EC4899", color:"white", padding:"10px 22px", borderRadius:50, fontSize:12, fontWeight:700, textDecoration:"none" }}>
            🗺️ Get Directions
          </a>
        </div>
        <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3752.3!2d75.3433!3d19.8762!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bdba305!2sGulmandi%20Aurangabad!5e0!3m2!1sen!2sin"
          width="100%" height="240" style={{ border:0, display:"block" }} allowFullScreen loading="lazy" title="Matchwell"/>
      </div>
      )}

      {/* Why Us */}
      <VideoRow sectionKey="showroomMap" onNav={onNav}/>
      {sectionVis.whyUs !== false && (
      <div style={{ padding:"40px clamp(14px,4vw,40px)", background:"#fafafa" }}>
        <div style={{ textAlign:"center", marginBottom:28 }}>
          <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:"clamp(20px,3vw,32px)", fontWeight:900, color:"#1a1a2e" }}>Why Shop at <em style={{ fontStyle:"italic", color:"#EC4899" }}>Matchwell?</em></h2>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))", gap:16 }}>
          {[{icon:"🏛️",t:"Since 1979",d:"Trusted by 3 generations"},{icon:"💎",t:"Premium Quality",d:"Handpicked designs"},{icon:"🚀",t:"Fast Delivery",d:"1-3 days across India"},{icon:"🔒",t:"Secure Payment",d:"UPI, Card & COD"},{icon:"↩️",t:"Easy Returns",d:"7-day hassle-free"},{icon:"💬",t:"WhatsApp Help",d:"9am–9pm daily"}].map((f,i)=>(
            <div key={i} style={{ background:"white", borderRadius:14, padding:"20px 16px", textAlign:"center", boxShadow:"0 2px 12px rgba(0,0,0,0.05)", transition:"transform 0.3s" }}
              onMouseEnter={e=>e.currentTarget.style.transform="translateY(-3px)"}
              onMouseLeave={e=>e.currentTarget.style.transform="none"}>
              <div style={{ fontSize:28, marginBottom:10 }}>{f.icon}</div>
              <div style={{ fontWeight:800, fontSize:13, color:"#1a1a2e", marginBottom:4 }}>{f.t}</div>
              <div style={{ fontSize:11, color:"#999", lineHeight:1.5 }}>{f.d}</div>
            </div>
          ))}
        </div>
      </div>
      )}

      <VideoRow sectionKey="whyUs" onNav={onNav}/>
      <Footer onNav={onNav}/>
    </div>
  );
}
